/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header, Footer } from "../components/Layout";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, Wallet, TagSimple, ArrowUp } from "@phosphor-icons/react";
import { toast } from "sonner";

// COBALT-WALLET theme — visually distinct from Recent Purchases (emerald)
const TH = {
  bg:        "#0A1224",
  surface:   "#13203B",
  surfaceHi: "#1B2E54",
  border:    "#243861",
  borderHi:  "#33518A",
  accent:    "#7DD3FC",      // cobalt
  accentDim: "#1E6CA8",
  warm:      "#FFD17B",
  pop:       "#A5B4FC",
  pink:      "#F472B6",
  green:     "#4ADE9C",
  text:      "#EAF2FB",
  textMute:  "#8DA0BD",
  textDim:   "#54688A",
};

const LIMITS = [10, 20, 30, 50];
const PAID_VIA_BADGE = {
  razorpay: { label: "Razorpay", color: "#7DD3FC" },
  paypal:   { label: "PayPal",   color: "#A5B4FC" },
};

function formatMoney(n, cur) {
  if (cur === "USD") return `$${(n || 0).toFixed(2)}`;
  return `\u20B9${(n || 0).toFixed(0)}`;
}

export default function AdminRecentTopupsPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [limit, setLimit] = useState(20);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user || !user.is_super_admin) {
      toast.error("Recent Top-ups is a Super-Admin tool.");
      navigate("/", { replace: true });
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (!user?.is_super_admin) return;
    setLoading(true);
    api.get("/super-admin/recent-topups", { params: { limit } })
      .then(({ data }) => setData(data))
      .catch((err) => toast.error(err?.response?.data?.detail || "Failed to load top-ups."))
      .finally(() => setLoading(false));
  }, [user, limit]);

  return (
    <div className="min-h-screen" style={{ backgroundColor: TH.bg, color: TH.text }}>
      <Header />
      <main className="mx-auto max-w-6xl px-4 sm:px-6 py-10 sm:py-14" data-testid="admin-recent-topups-page">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1 text-xs mb-4 hover:opacity-80"
          style={{ color: TH.textMute }}
          data-testid="back-btn"
        >
          <ArrowLeft size={12} /> Back
        </button>

        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: TH.surfaceHi }}>
            <Wallet size={20} weight="duotone" style={{ color: TH.accent }} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em]" style={{ color: TH.accent }}>Super Admin</div>
            <h1 className="font-serif-display text-3xl sm:text-4xl font-light">Recent Top-ups</h1>
          </div>
        </div>
        <p className="text-sm mt-2 mb-6" style={{ color: TH.textMute }}>
          Last successful wallet recharges across Razorpay (INR) &amp; PayPal (USD).
          Use this to monitor cashflow, spot fraud patterns, and see which coupons drive recharges.
        </p>

        <div className="flex items-center gap-2 mb-5">
          <span className="text-xs" style={{ color: TH.textMute }}>Show:</span>
          {LIMITS.map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => setLimit(n)}
              className="px-2.5 py-1 rounded text-xs font-mono transition"
              style={{
                backgroundColor: limit === n ? TH.accent : TH.surface,
                color: limit === n ? TH.bg : TH.text,
                border: `1px solid ${limit === n ? TH.accent : TH.border}`,
              }}
              data-testid={`limit-${n}`}
            >
              {n}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-12" style={{ color: TH.textMute }} data-testid="loading">
            Loading top-ups&hellip;
          </div>
        ) : !data || !data.topups?.length ? (
          <div className="text-center py-12" style={{ color: TH.textMute }} data-testid="empty">
            No top-ups recorded yet.
          </div>
        ) : (
          <>
            <div className="mb-4 px-4 py-3 rounded-lg flex items-center justify-between"
                 style={{ backgroundColor: TH.surfaceHi, border: `1px solid ${TH.border}` }}
                 data-testid="topups-summary">
              <div>
                <div className="text-[10px] uppercase tracking-wider" style={{ color: TH.textMute }}>Total (INR equivalent)</div>
                <div className="text-2xl font-mono font-bold" style={{ color: TH.accent }}>
                  &#8377;{(data.total_inr_equivalent || 0).toLocaleString("en-IN")}
                </div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-wider" style={{ color: TH.textMute }}>Count</div>
                <div className="text-2xl font-mono">{data.count}</div>
              </div>
            </div>

            <div className="space-y-2" data-testid="topups-list">
              {data.topups.map((r, i) => {
                const paidVia = PAID_VIA_BADGE[r.paid_via] || { label: r.paid_via, color: TH.textMute };
                const hasCoupon = !!r.coupon_code;
                return (
                  <div
                    key={r.order_id || i}
                    className="rounded-lg p-3 sm:p-4"
                    style={{ backgroundColor: TH.surface, border: `1px solid ${TH.border}` }}
                    data-testid={`topup-row-${i}`}
                  >
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-semibold" style={{ color: TH.text }}>
                            {r.customer_name}
                          </span>
                          <span className="text-[11px]" style={{ color: TH.textMute }}>
                            {r.customer_email}
                          </span>
                        </div>
                        <div className="text-[10px] mt-1 font-mono" style={{ color: TH.textDim }}>
                          {r.order_id} &middot; {r.credited_at ? new Date(r.credited_at).toLocaleString() : "—"}
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="text-lg font-mono font-bold" style={{ color: TH.accent }}>
                          {formatMoney(r.amount, r.currency)}
                        </div>
                        <span className="inline-block text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded mt-1"
                              style={{ backgroundColor: `${paidVia.color}22`, color: paidVia.color }}>
                          {paidVia.label}
                        </span>
                      </div>
                    </div>

                    {/* Math breakdown row */}
                    <div className="mt-3 pt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px] border-t" style={{ borderColor: TH.border }}>
                      <div>
                        <div style={{ color: TH.textMute }}>Recharge</div>
                        <div className="font-mono" style={{ color: TH.text }}>{formatMoney(r.amount, r.currency)}</div>
                      </div>
                      <div>
                        <div style={{ color: TH.textMute }}>GST</div>
                        <div className="font-mono" style={{ color: TH.text }}>{formatMoney(r.gst_amount, r.currency)}</div>
                      </div>
                      <div>
                        <div style={{ color: TH.textMute }}>Pack bonus</div>
                        <div className="font-mono" style={{ color: TH.green }}>
                          {r.pack_bonus > 0 ? `+${formatMoney(r.pack_bonus, r.currency)}` : "—"}
                        </div>
                      </div>
                      <div>
                        <div style={{ color: TH.textMute }}>Credited</div>
                        <div className="font-mono font-bold flex items-center gap-1" style={{ color: TH.green }}>
                          <ArrowUp size={10} weight="bold" />
                          {formatMoney(r.credited_amount, r.currency)}
                        </div>
                      </div>
                    </div>

                    {hasCoupon && (
                      <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] font-semibold"
                           style={{ backgroundColor: `${TH.warm}22`, color: TH.warm }}>
                        <TagSimple size={11} weight="bold" />
                        {r.coupon_code} &middot; +{formatMoney(r.coupon_bonus, r.currency)} bonus
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
